<template>
  <v-parallax class="hero" src="/img/hero-room.webp" alt="Jasmine Therapies treatment room">
    <div class="hero-banner fill-height d-flex flex-column justify-center">
      <div class="intro-text d-flex flex-column justify-center pt-0 pa-8">
        <div class="text-center text-subtitle-1 pa-2">You are one step away from</div>
        <div class="text-center text-h2 pa-2">comfortable, lasting relief</div>
      </div>
      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.PainReliefMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </v-parallax>

  <section id="why-choose">
    <div class="full-width-header">Why choose our pain relief massage?</div>
    <div class="content pb-16">
      <div class="feature-grid">
        <div class="feature-item" v-for="item in whyChoose" :key="item.title">
          <div class="feature-title">{{ item.title }}</div>
          <div class="feature-text">{{ item.description }}</div>
        </div>
      </div>

      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.PainReliefMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>

  <section id="trusted-by">
    <div class="full-width-header">Trusted by our clients</div>
    <div class="content pb-16">
      <div class="quote-block" v-for="review in featureReviews" :key="review.name">
        <img class="quote-stars" :src="starsImage" alt="Five stars" />
        <p class="quote-text" v-html="review.quote"></p>
        <div class="quote-author">{{ review.name }}</div>
      </div>

      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.PainReliefMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>

  <BenefitsSectionComponent
    header="What are the benefits of Pain Relief Massage?"
    :benefits="benefits"
    :booking-url="$bookingUrls.PainReliefMassage.Default" />

  <section id="pricing">
    <div class="full-width-header">Individual Sessions & Packages</div>
    <div class="content pb-16">
      <p class="content-intro">
        Choose the option that fits your goals best. Our pain relief massage sessions are designed to ease tension,
        improve mobility, and help you feel more like yourself again.
      </p>

      <div class="service-list d-flex flex-wrap justify-center ga-12 px-8">
        <ServiceItemComponent
          title="5-Session Packages"
          :booking-options="packageOptions"
          :link-target="'_self'"
          class="service-item-card" />

        <ServiceItemComponent
          title="Individual Sessions"
          :booking-options="sessionOptions"
          :link-target="'_self'"
          class="service-item-card" />
      </div>
    </div>
  </section>

  <section id="cta">
    <div class="full-width-header">Ready to feel relief?</div>
    <div class="content pb-16">
      <p class="content-intro">
        Book your pain relief massage today and experience targeted care that supports your recovery and comfort.
      </p>
      <div class="text-center">
        <v-btn class="book-now-button" color="primary" size="large" :href="$bookingUrls.PainReliefMassage.Default">Book now!</v-btn>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { BookingUrls } from '@/constants/booking';
import BookingOption from '@/models/BookingOption';
import BenefitsSectionComponent from '@/components/BenefitsSectionComponent.vue';
import ServiceItemComponent from '@/components/ServiceItemComponent.vue';

export default defineComponent({
  name: 'PainReliefMassageView',

  components: {
    BenefitsSectionComponent,
    ServiceItemComponent
  },

  data() {
    return {
      whyChoose: [
        {
          title: 'Targeted Relief',
          description: 'We focus on the areas that need it most, using tailored pressure and techniques.'
        },
        {
          title: 'Experienced Care',
          description: 'Years of hands-on expertise in relieving persistent tension and discomfort.'
        },
        {
          title: 'Supportive Recovery',
          description: 'Ideal for ongoing aches, postural strain, and restoring comfortable movement.'
        }
      ],
      starsImage: '/img/five-stars.png',
      featureReviews: [
        {
          name: 'Ana Miljanić',
          quote:
            'Fantastic massage. I had pain relief and Jasmine really concentrated on painful areas and the pain is gone.<br><br>Ambient is very relaxing and nicely decorated and Jasmine really knows what\'s she\'s doing.'
        },
        {
          name: 'Shane O Keeffe',
          quote:
            'Had multiple pain relief massages with Jasmine. Helps me with many different issues I have had and is very knowledgeable in why these issues start in the first place. Definitely the best place I have ever been to for pain relief. Highly recommend.'
        }
      ],
      benefits: [
        {
          title: 'Targets Chronic Pain',
          description: 'Focused deep tissue techniques address persistent pain at its source, providing meaningful and lasting relief.'
        },
        {
          title: 'Improves Mobility',
          description: 'Releases muscle restrictions and joint stiffness, helping you move more freely and comfortably in daily life.'
        },
        {
          title: 'Speeds Recovery',
          description: 'Enhances blood flow to affected areas, supporting faster healing and reducing inflammation.'
        }
      ],
      packageOptions: [
        new BookingOption('5 x 50 min', BookingUrls.PainReliefMassage.Package50),
        new BookingOption('5 x 1h 15min', BookingUrls.PainReliefMassage.Package75)
      ],
      sessionOptions: [
        new BookingOption('50 min', BookingUrls.PainReliefMassage.Min50),
        new BookingOption('1h 15min', BookingUrls.PainReliefMassage.Min75)
      ]
    };
  }
});
</script>
